import {
  ICON_ALERT_CIRCLE_OUTLINE, ICON_CHECK_CIRCLE_OUTLINE, ICON_INFORMATION_OUTLINE,
  MessageType,
  TextColor,
} from '@doveit/bricks';
import { GoalMetric, PerformanceRating } from '../../../providers/api/dtos';

type ConfigByMetricValue = {
  metricLabel: string,
  metricVerb: string,
  metricFrequency: string,
  progressStatusText: string
};

export const configByRating: Record<PerformanceRating, {
  color: TextColor,
  current: string,
  forecast: string,
  hint: {
    text: string,
    icon: string,
    messageType: MessageType
  }
}> = {
  CRITICAL: {
    color: 'critical.default.low',
    current: 'Ad oggi avrebbe dovuto {METRIC_VERB} {EXPECTED} {METRIC_LABEL}, è in ritardo di {DELTA}.  \nLa media è di **{AVERAGE} {METRIC_LABEL} {METRIC_FREQUENCY}**, ben al di sotto della **media richiesta di {EXPECTED_AVERAGE}**.',
    forecast: 'Per completare l\'obiettivo, dovrà ora **{METRIC_VERB} {FORECAST} {METRIC_LABEL} {METRIC_FREQUENCY}** da qui a fine anno.',
    hint: {
      text: 'La situazione è critica: serve un cambio di marcia immediato',
      icon: ICON_ALERT_CIRCLE_OUTLINE,
      messageType: 'critical',
    },
  },
  BELOW_EXPECTATIONS: {
    color: 'warning.default.low',
    current: 'Ad oggi avrebbe dovuto {METRIC_VERB} {EXPECTED} {METRIC_LABEL}, è in ritardo di {DELTA}.  \nLa media è di **{AVERAGE} {METRIC_LABEL} {METRIC_FREQUENCY}**, sotto la **media richiesta di {EXPECTED_AVERAGE}**.',
    forecast: 'Per completare l\'obiettivo, dovrà ora **{METRIC_VERB} {FORECAST} {METRIC_LABEL} {METRIC_FREQUENCY}** da qui a fine anno.',
    hint: {
      text: 'La situazione è a rischio: serve costanza e attenzione',
      icon: ICON_ALERT_CIRCLE_OUTLINE,
      messageType: 'warning',
    },
  },
  NEARLY_ON_TRACK: {
    color: 'info.default.low',
    current: 'Ad oggi avrebbe dovuto {METRIC_VERB} {EXPECTED} {METRIC_LABEL}, è leggermente in ritardo di {DELTA}.  \nLa media è di **{AVERAGE} {METRIC_LABEL} {METRIC_FREQUENCY}**, a fronte di una **media richiesta di {EXPECTED_AVERAGE}**.',
    forecast: 'Per completare l\'obiettivo, dovrà ora **{METRIC_VERB} {FORECAST} {METRIC_LABEL} {METRIC_FREQUENCY}** da qui a fine anno.',
    hint: {
      text: 'Continua così: basta un piccolo sforzo in più',
      icon: ICON_INFORMATION_OUTLINE,
      messageType: 'info',
    },
  },
  ON_TRACK: {
    color: 'info.default.low',
    current: 'Ad oggi avrebbe dovuto {METRIC_VERB} {EXPECTED} {METRIC_LABEL}, è in anticipo di {DELTA}.  \nLa media è di **{AVERAGE} {METRIC_LABEL} {METRIC_FREQUENCY}**, a fronte di una **media richiesta di {EXPECTED_AVERAGE}**.',
    forecast: 'Per completare l\'obiettivo, dovrà ora **{METRIC_VERB} {FORECAST} {METRIC_LABEL} {METRIC_FREQUENCY}** da qui a fine anno.',
    hint: {
      text: 'Continua così: sei in linea con il piano',
      icon: ICON_INFORMATION_OUTLINE,
      messageType: 'info',
    },
  },
  ABOVE_EXPECTATIONS: {
    color: 'success.default.low',
    current: 'Ad oggi avrebbe dovuto {METRIC_VERB} {EXPECTED} {METRIC_LABEL}, è in anticipo di {DELTA}.  \nLa media è di **{AVERAGE} {METRIC_LABEL} {METRIC_FREQUENCY}**, a fronte di una **media richiesta di {EXPECTED_AVERAGE}**.',
    forecast: 'Per completare l\'obiettivo, dovrà ora **{METRIC_VERB} {FORECAST} {METRIC_LABEL} {METRIC_FREQUENCY}** da qui a fine anno.',
    hint: {
      text: 'Ottimo lavoro: sei avanti rispetto al piano',
      icon: ICON_CHECK_CIRCLE_OUTLINE,
      messageType: 'success',
    },
  },
};

export const configByMetric: Record<GoalMetric, ConfigByMetricValue> = {
  rumors: {
    progressStatusText: 'Il team ha inserito {PERFORMANCE} notizie su un obiettivo di {GOAL}',
    metricLabel: 'notizie',
    metricVerb: 'inserire',
    metricFrequency: 'a settimana',
  },
  prospects: {
    progressStatusText: 'Il team ha effettuato {PERFORMANCE} valutazioni su un obiettivo di {GOAL}',
    metricLabel: 'valutazioni',
    metricVerb: 'effettuare',
    metricFrequency: 'a settimana',

  },
  assignments: {
    progressStatusText: 'Il team ha acquisito {PERFORMANCE} mandati su un obiettivo di {GOAL}',
    metricLabel: 'mandati',
    metricVerb: 'acquisire',
    metricFrequency: 'al mese',
  },
  sales: {
    progressStatusText: 'Il team ha concluso {PERFORMANCE} vendite su un obiettivo di {GOAL}',
    metricLabel: 'vendite',
    metricVerb: 'concludere',
    metricFrequency: 'al mese',
  },
};

/** TODO remove comments when learning platform link will be ready */
export const configByMetricAndStatus: Record<GoalMetric, Record<PerformanceRating, any>> = {
  rumors: {
    CRITICAL: '',
    BELOW_EXPECTATIONS: '',
    NEARLY_ON_TRACK: 'Potrai raggiungere l\'obiettivo annuale con successo aumentando di poco la tua media.',
    ON_TRACK: 'Potrai raggiungere l\'obiettivo annuale con successo mantenendo questo ritmo.',
    ABOVE_EXPECTATIONS: 'Potresti chiudere l\'obiettivo in anticipo ed andare al di là delle iniziali aspettative. Continua così!',
  },
  prospects: {
    CRITICAL: '',
    BELOW_EXPECTATIONS: '',
    NEARLY_ON_TRACK: 'Potrai raggiungere l\'obiettivo annuale con successo aumentando di poco la tua media.',
    ON_TRACK: 'Potrai raggiungere l\'obiettivo annuale con successo mantenendo questo ritmo.',
    ABOVE_EXPECTATIONS: 'Potresti chiudere l\'obiettivo in anticipo ed andare al di là delle iniziali aspettative. Continua così!',
  },
  assignments: {
    CRITICAL: '',
    BELOW_EXPECTATIONS: '',
    NEARLY_ON_TRACK: 'Potrai raggiungere l\'obiettivo annuale con successo aumentando di poco la tua media.',
    ON_TRACK: 'Potrai raggiungere l\'obiettivo annuale con successo mantenendo questo ritmo.',
    ABOVE_EXPECTATIONS: 'Potresti chiudere l\'obiettivo in anticipo ed andare al di là delle iniziali aspettative. Continua così!',
  },
  sales: {
    CRITICAL: '',
    BELOW_EXPECTATIONS: '',
    NEARLY_ON_TRACK: 'Potrai raggiungere l\'obiettivo annuale con successo aumentando di poco la tua media.',
    ON_TRACK: 'Potrai raggiungere l\'obiettivo annuale con successo mantenendo questo ritmo.',
    ABOVE_EXPECTATIONS: 'Potresti chiudere l\'obiettivo in anticipo ed andare al di là delle iniziali aspettative. Continua così!',
  },
};
